export function formatCapitalCase(str: string): string {
  if (!str) return str;

  const result = str.toLowerCase();
  return result.charAt(0).toUpperCase() + result.slice(1);
}

export function formatTitleCase(s: string) {
  return s.split(' ').map(formatCapitalCase).join(' ');
}

function isDefinedString<T>(
  value: T,
): value is Exclude<T, null | undefined | ''> {
  return value !== null && value !== undefined && (value as any) !== '';
}

/** Adds percent symbol `%` to a given value. */
function formatPercent(value: string | number): string {
  return `${value}%`;
}

/**
 * Adds percent symbol `%` to a given value.
 * Defaults to empty string `""` if not defined.
 */
export function formatPercentIfDefined(
  value: string | number | null | undefined,
) {
  return isDefinedString(value) ? formatPercent(value) : '';
}

interface FormatCurrencyOptions {
  places: number;
  decimal: string;
  separator: string;
  symbol: string;
}

export function formatCurrency(
  total: string | number,
  formatOptions?: Partial<FormatCurrencyOptions>,
): string {
  const {
    places = 2,
    decimal = '.',
    separator = ',',
    symbol = '$',
  } = formatOptions || {};

  if (typeof total === 'string' && total.match(/[\,\$]/)) {
    // This value has *already* been formatted. Return as is.
    return total;
  }

  const amount = (Math.abs(+total) || 0).toFixed(places);

  const i = `${parseInt(amount, 10)}`;
  const j = i.length > 3 ? i.length % 3 : 0;

  const result =
    symbol +
    (j ? i.substr(0, j) + separator : '') +
    i.substr(j).replace(/(\d{3})(?=\d)/g, `$1${separator}`) +
    (places
      ? decimal +
        Math.abs(+amount - +i)
          .toFixed(places)
          .slice(2)
      : '');

  return +total < 0 ? `(${result})` : result;
}

/**
 * Applies `USD` currency formatting to a given value.
 * Defaults to empty string `""` if not defined.
 */
export function formatCurrencyIfDefined(
  value: string | number | null | undefined,
) {
  return isDefinedString(value) ? formatCurrency(value) : '';
}

export function parseDate(dateString: string): Date {
  const date = new Date(Date.parse(dateString.replace(' ', 'T')));
  const offset = date.getTimezoneOffset() / 60;
  date.setHours(date.getHours() - offset);
  return date;
}

export function formatDateWithTimezone(timestamp: string): string {
  const date = parseDate(timestamp);

  const options = {
    timeZoneName: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  } as const;

  return date.toLocaleString('en-US', options);
}

export function s4(): string {
  return Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .substring(1);
}

export function uuid(): string {
  return `${s4()}-${s4()}-${s4()}-${s4()}-${s4()}`;
}
